/* eslint-disable jsx-a11y/label-has-associated-control */
import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import SVG from 'react-inlinesvg';

import { validateField, normalizeField, cardUtility } from '../../../helpers/forms';
import FormField from './FormField';
import SavedCardForm from './SavedCardForm';
import FullCardForm from './FullCardForm';
import visaLogo from '../UpsellVariantB/UpsellForm/assets/visa-inc-logo.svg';
import mastercardLogo from '../UpsellVariantB/UpsellForm/assets/mc-symbol.svg';

import { FormWrapper, CardSelectorRadio, StyledHideForm } from './FormFields.styles';

export const fields = {
  name: {
    id: 'nameOnCreditCard',
    dataAutomationTestElement: 'nameOnCreditCard',
    component: FormField,
    props: { maxLength: 150 },
    validate: validateField.required,
    normalize: normalizeField.formatName,
    errorMessageProps: {
      required: ['the name on your card'],
    },
    label: 'Name as shown on card',
  },
  ccNumber: {
    id: 'creditCardNumber',
    dataAutomationTestElement: 'creditCardNumber',
    component: FormField,
    props: {
      maxLength: 19,
      placeholder: '0000 0000 0000 0000',
      isCCNumberInput: true,
    },
    inputMode: 'numeric',
    validate: validateField.cardNumber,
    normalize: cardUtility.formatNumber,
    onChange: (event, change) => change('creditCardType', cardUtility.returnCardType(event.target.value)),
    errorMessageProps: {
      required: ['your Visa or Mastercard number'],
      format: ['Visa or Mastercard number'],
    },
    label: 'Card number',
    images: ['mastercard', 'visa'],
  },
  expiry: {
    id: 'expiryDate',
    dataAutomationTestElement: 'expiryDate',
    component: FormField,
    props: {
      maxLength: 5,
      placeholder: 'MM/YY',
    },
    inputMode: 'numeric',
    validate: validateField.expiryDate,
    normalize: normalizeField.date,
    errorMessageProps: {
      required: ['card expiry date'],
      format: ['card expiry date'],
    },
    label: 'Expiry date',
  },
  cvv: {
    id: 'CVV',
    component: FormField,
    props: { maxLength: 3 },
    inputMode: 'numeric',
    validate: validateField.cvvNumber,
    normalize: normalizeField.numeric,
    errorMessageProps: {
      required: ['your CVV number'],
      format: ['your CVV number'],
    },
    label: 'Security Code',
  },
};

const FormFields = props => {
  const { savedCard, useSavedCard, hasSavedCard, handleRadioSwitch, setCvv } = props;

  const cardLogo = cardType => {
    if (cardType && (cardType.toLocaleLowerCase() === 'mc' || cardType.toLocaleLowerCase() === 'mastercard'))
      return mastercardLogo;
    if (cardType && cardType.toLowerCase() === 'visa') return visaLogo;
    return null;
  };

  const cardType = savedCard?.creditCardType;

  return (
    <FormWrapper>
      {hasSavedCard && (
        <React.Fragment>
          <CardSelectorRadio data-automation-test-element="use-saved-card">
            <input
              type="radio"
              id="saved_card_existing_card"
              checked={useSavedCard}
              name="saved_card"
              value="true"
              onChange={handleRadioSwitch}
            />
            <label htmlFor="saved_card_existing_card">
              <SVG
                id="card-logo-img"
                alt={cardType}
                src={cardLogo(cardType)}
                data-automation-test-element={`${cardType?.toLowerCase()}-logo`}
              />
              <svg
                className="dots"
                width="44"
                height="8"
                viewBox="0 0 44 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.06402 7.306C3.57202 7.306 3.11002 7.213 2.67802 7.027C2.24602 6.847 1.86502 6.595 1.53502 6.271C1.21102 5.941 0.956016 5.56 0.770016 5.128C0.590016 4.696 0.500016 4.234 0.500016 3.742C0.500016 3.244 0.590016 2.782 0.770016 2.356C0.956016 1.924 1.21102 1.546 1.53502 1.222C1.86502 0.892 2.24602 0.637 2.67802 0.456999C3.11002 0.271 3.57202 0.178 4.06402 0.178C4.56202 0.178 5.02402 0.271 5.45002 0.456999C5.88202 0.637 6.26002 0.892 6.58402 1.222C6.91402 1.546 7.16902 1.924 7.34902 2.356C7.53502 2.782 7.62802 3.244 7.62802 3.742C7.62802 4.234 7.53502 4.696 7.34902 5.128C7.16902 5.56 6.91402 5.941 6.58402 6.271C6.26002 6.595 5.88202 6.847 5.45002 7.027C5.02402 7.213 4.56202 7.306 4.06402 7.306ZM16.106 7.306C15.614 7.306 15.152 7.213 14.72 7.027C14.288 6.847 13.907 6.595 13.577 6.271C13.253 5.941 12.998 5.56 12.812 5.128C12.632 4.696 12.542 4.234 12.542 3.742C12.542 3.244 12.632 2.782 12.812 2.356C12.998 1.924 13.253 1.546 13.577 1.222C13.907 0.892 14.288 0.637 14.72 0.456999C15.152 0.271 15.614 0.178 16.106 0.178C16.604 0.178 17.066 0.271 17.492 0.456999C17.924 0.637 18.302 0.892 18.626 1.222C18.956 1.546 19.211 1.924 19.391 2.356C19.577 2.782 19.67 3.244 19.67 3.742C19.67 4.234 19.577 4.696 19.391 5.128C19.211 5.56 18.956 5.941 18.626 6.271C18.302 6.595 17.924 6.847 17.492 7.027C17.066 7.213 16.604 7.306 16.106 7.306ZM28.148 7.306C27.656 7.306 27.194 7.213 26.762 7.027C26.33 6.847 25.949 6.595 25.619 6.271C25.295 5.941 25.04 5.56 24.854 5.128C24.674 4.696 24.584 4.234 24.584 3.742C24.584 3.244 24.674 2.782 24.854 2.356C25.04 1.924 25.295 1.546 25.619 1.222C25.949 0.892 26.33 0.637 26.762 0.456999C27.194 0.271 27.656 0.178 28.148 0.178C28.646 0.178 29.108 0.271 29.534 0.456999C29.966 0.637 30.344 0.892 30.668 1.222C30.998 1.546 31.253 1.924 31.433 2.356C31.619 2.782 31.712 3.244 31.712 3.742C31.712 4.234 31.619 4.696 31.433 5.128C31.253 5.56 30.998 5.941 30.668 6.271C30.344 6.595 29.966 6.847 29.534 7.027C29.108 7.213 28.646 7.306 28.148 7.306ZM40.19 7.306C39.698 7.306 39.236 7.213 38.804 7.027C38.372 6.847 37.991 6.595 37.661 6.271C37.337 5.941 37.082 5.56 36.896 5.128C36.716 4.696 36.626 4.234 36.626 3.742C36.626 3.244 36.716 2.782 36.896 2.356C37.082 1.924 37.337 1.546 37.661 1.222C37.991 0.892 38.372 0.637 38.804 0.456999C39.236 0.271 39.698 0.178 40.19 0.178C40.688 0.178 41.15 0.271 41.576 0.456999C42.008 0.637 42.386 0.892 42.71 1.222C43.04 1.546 43.295 1.924 43.475 2.356C43.661 2.782 43.754 3.244 43.754 3.742C43.754 4.234 43.661 4.696 43.475 5.128C43.295 5.56 43.04 5.941 42.71 6.271C42.386 6.595 42.008 6.847 41.576 7.027C41.15 7.213 40.688 7.306 40.19 7.306Z"
                  fill="#63666A"
                />
              </svg>
              <div data-automation-test-element="card-number-ending">{savedCard?.cardNumberEnding}</div>
              <div>&nbsp;|&nbsp;</div>
              <div data-automation-test-element="expiry-date">
                Exp. {moment(`${savedCard?.expirationMonth}/${savedCard?.expirationYear}`, 'MM/YYYY').format('MM/YY')}
              </div>
            </label>
          </CardSelectorRadio>

          <StyledHideForm visible={useSavedCard} data-automation-test-element="saved-card-form-hider">
            <SavedCardForm setCvv={setCvv} data-automation-test-element="saved-card-form" />
          </StyledHideForm>

          <hr />
        </React.Fragment>
      )}

      <React.Fragment>
        {hasSavedCard && (
          <CardSelectorRadio data-automation-test-element="use-different-card">
            <input
              type="radio"
              id="saved_card"
              checked={!useSavedCard}
              name="saved_card"
              value="false"
              onChange={handleRadioSwitch}
            />
            <label htmlFor="saved_card">Use a different card</label>
          </CardSelectorRadio>
        )}

        <StyledHideForm visible={!useSavedCard || !hasSavedCard} data-automation-test-element="full-card-form-hider">
          <FullCardForm />
        </StyledHideForm>
      </React.Fragment>
    </FormWrapper>
  );
};

FormFields.propTypes = {
  savedCard: PropTypes.shape({
    billingProviderPaymentMethodId: PropTypes.string,
    cardNumberEnding: PropTypes.string,
    creditCardNumber: PropTypes.string,
    creditCardType: PropTypes.string,
    cvv: PropTypes.number,
    expirationMonth: PropTypes.number,
    expirationYear: PropTypes.number,
    isDefaultCard: PropTypes.bool,
    nameOnCreditCard: PropTypes.string,
    source: PropTypes.string,
  }),
  useSavedCard: PropTypes.bool.isRequired,
  hasSavedCard: PropTypes.bool,
  handleRadioSwitch: PropTypes.func.isRequired,
  setCvv: PropTypes.func.isRequired,
};

FormFields.defaultProps = {
  savedCard: {
    creditCardType: '',
    cardNumberEnding: '',
    expirationMonth: '',
    expirationYear: '',
  },
  hasSavedCard: undefined,
};

export default FormFields;
